import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Compass, Route, MapPin, Navigation, Mountain,
  AlertTriangle, Tent, Car, Droplets, Binoculars,
  UtensilsCrossed, Star, Check, Plug, Waves, ShieldAlert, Flame,
} from 'lucide-react';
import { RouteFollowPoi, WaypointIcon, ElevationPoint } from '../../types';
import { formatDistance } from '../../services/trackService';
import type { ContextualAlert } from '../../hooks/useContextualAlerts';

import { BRAND } from '../../constants/mapColors';
type MapOrientationMode = 'north_up' | 'heading_up';
type FollowViewMode = '2d' | '3d';

const WAYPOINT_BUTTONS: { icon: WaypointIcon; Icon: React.FC<any>; color: string; label: string }[] = [
  { icon: 'camp',      Icon: Tent,           color: 'text-emerald-500', label: 'Leir' },
  { icon: 'parking',   Icon: Car,            color: 'text-blue-500',    label: 'Parkering' },
  { icon: 'water',     Icon: Droplets,       color: 'text-cyan-500',    label: 'Vann' },
  { icon: 'viewpoint', Icon: Binoculars,     color: 'text-amber-500',   label: 'Utsikt' },
  { icon: 'pin',       Icon: MapPin,         color: 'text-red-500',     label: 'Punkt' },
  { icon: 'star',      Icon: Star,           color: 'text-yellow-500',  label: 'Favoritt' },
];

interface TabletFollowPanelProps {
  mode: 'route_follow' | 'free_drive_follow';
  routeName: string;
  progressM: number | null;
  totalDistanceM: number;
  distanceFromRoute: number | null;
  nearestPointDistanceAlongM: number | null;
  nextPois: RouteFollowPoi[];
  activeTargetName?: string | null;
  activeTargetDistanceM?: number | null;
  mapOrientationMode: MapOrientationMode;
  onToggleMapOrientation: () => void;
  viewMode: FollowViewMode;
  onToggleViewMode: () => void;
  isOffRoute?: boolean;
  contextualAlerts?: ContextualAlert[];
  onAddWaypoint?: (icon: WaypointIcon) => void;
  waypointCount?: number;
  elevationProfile?: ElevationPoint[];
}

const MiniElevationChart: React.FC<{ profile: ElevationPoint[] }> = ({ profile }) => {
  const { t } = useTranslation();
  if (profile.length < 2) return null;
  const W = 280; const H = 40;
  const maxDist = profile[profile.length - 1].distance;
  const eles = profile.map(p => p.elevation);
  const minE = Math.min(...eles); const maxE = Math.max(...eles);
  const range = maxE - minE || 1;
  const pts = profile.map(p => {
    const x = (p.distance / maxDist) * W;
    const y = H - ((p.elevation - minE) / range) * (H - 8) - 4;
    return `${x},${y}`;
  }).join(' ');
  const fillD = `M ${pts} L ${W},${H} L 0,${H} Z`;
  const gain = eles.reduce((g, e, i) => i > 0 && e > eles[i - 1] ? g + (e - eles[i - 1]) : g, 0);
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-3xs font-black text-[var(--text-secondary)] uppercase tracking-widest">{t("auto.routes.tabletfollowpanel.hoydeprofil", "Høydeprofil")}</span>
        <span className="text-3xs font-black text-emerald-500">+{Math.round(gain)}m</span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-10 overflow-visible" preserveAspectRatio="none">
        <defs>
          <linearGradient id="tabletEleGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={BRAND} stopOpacity="0.35" />
            <stop offset="100%" stopColor={BRAND} stopOpacity="0.05" />
          </linearGradient>
        </defs>
        <path d={fillD} fill="url(#tabletEleGrad)" />
        <polyline points={pts} fill="none" stroke={BRAND} strokeWidth="2" vectorEffect="non-scaling-stroke" strokeLinejoin="round" />
      </svg>
      <div className="flex justify-between text-4xs text-[var(--text-muted)] font-bold mt-0.5">
        <span>{Math.round(minE)}m</span>
        <span>{Math.round(maxE)}m</span>
      </div>
    </div>
  );
};

const TabletFollowPanel: React.FC<TabletFollowPanelProps> = ({
  routeName,
  mode,
  progressM,
  totalDistanceM,
  distanceFromRoute,
  nearestPointDistanceAlongM,
  nextPois,
  activeTargetName,
  activeTargetDistanceM,
  mapOrientationMode,
  onToggleMapOrientation,
  viewMode,
  onToggleViewMode,
  isOffRoute = false,
  contextualAlerts = [],
  onAddWaypoint,
  waypointCount = 0,
  elevationProfile,
}) => {
  const { t } = useTranslation();
  const [justAdded, setJustAdded] = useState<WaypointIcon | null>(null);

  const progressPercent = totalDistanceM > 0 && progressM !== null
    ? Math.min(100, (progressM / totalDistanceM) * 100)
    : 0;

  const handleAddWaypoint = (icon: WaypointIcon) => {
    onAddWaypoint?.(icon);
    setJustAdded(icon);
    setTimeout(() => setJustAdded(null), 1200);
  };

  return (
    <div className={`absolute left-4 top-4 bottom-4 z-[3600] w-[320px] border rounded-3xl shadow-2xl overflow-hidden flex flex-col ${
      isOffRoute
        ? 'bg-red-50 border-red-400'
        : 'bg-white border-stone-200'
    }`}>
      {/* Header */}
      <div className="px-4 py-3 border-b border-stone-100 shrink-0">
        <div className="text-3xs font-semibold uppercase tracking-wide text-stone-400 flex items-center gap-1">
          <Route size={10} /> {mode === 'route_follow' ? 'Aktiv rute' : 'Kjørevisning'}
        </div>
        <div className="text-[14px] font-black text-stone-800 truncate mt-1">{mode === 'route_follow' ? routeName : 'Kart følger GPS'}</div>
      </div>

      {/* Off-route alert */}
      {isOffRoute && distanceFromRoute !== null && (
        <div className="mx-4 mt-3 flex items-center gap-2 bg-red-100 border border-red-300 rounded-xl px-3 py-2 animate-pulse shrink-0">
          <AlertTriangle size={16} className="text-red-500 flex-shrink-0" />
          <div>
            <span className="text-[12px] text-red-700 font-black block">
              {Math.round(distanceFromRoute)}m fra ruten
            </span>
            <span className="text-3xs text-red-500 font-medium">{t("auto.routes.tabletfollowpanel.ga_tilbake_til_ruten", "Gå tilbake til ruten")}</span>
          </div>
        </div>
      )}

      {/* Contextual alerts */}
      {contextualAlerts.length > 0 && (
        <div className="mx-4 mt-2 space-y-1.5 shrink-0">
          {contextualAlerts.map(alert => {
            const IconComp = alert.iconName === 'Plug' ? Plug
              : alert.iconName === 'Waves' ? Waves
              : alert.iconName === 'ShieldAlert' ? ShieldAlert
              : Flame;
            return (
              <div
                key={alert.id}
                className={`flex items-center gap-2 ${alert.bgColor} border ${alert.borderColor} rounded-xl px-3 py-2`}
              >
                <IconComp size={14} className={alert.color} />
                <div className="min-w-0 flex-1">
                  <span className={`text-tiny font-bold ${alert.color} block leading-tight`}>
                    {alert.title}
                  </span>
                  {alert.subtitle && (
                    <span className="text-3xs text-stone-500 block leading-tight truncate">
                      {alert.subtitle}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto" style={{ overscrollBehavior: 'contain' }}>
        {/* Route progress */}
        {mode === 'route_follow' && (
          <div className="px-4 py-3 border-b border-stone-100">
            <div className="flex items-center justify-between text-2xs font-bold text-[var(--text-muted)] mb-1.5">
              <span>{formatDistance(progressM ?? 0)}</span>
              <span>{formatDistance(totalDistanceM)}</span>
            </div>
            <div className="w-full h-2 rounded-full bg-stone-100 overflow-hidden">
              <div className="h-full bg-emerald-500 rounded-full transition-all duration-700" style={{ width: `${progressPercent}%` }} />
            </div>
          </div>
        )}

        {/* Active target */}
        <div className="px-4 py-3 border-b border-stone-100">
          <div className="text-3xs font-semibold uppercase tracking-wide text-stone-400 mb-2">
            {mode === 'route_follow' ? 'Aktuelt punkt' : 'Nærmeste punkt'}
          </div>
          <div className="flex items-center gap-2 text-[13px] font-bold text-stone-800 min-w-0">
            <MapPin size={14} className="text-blue-600" />
            <span className="truncate">{activeTargetName || 'Ingen punkt valgt'}</span>
          </div>
          <div className="text-tiny text-[var(--text-secondary)] mt-1">
            {activeTargetDistanceM !== null && activeTargetDistanceM !== undefined
              ? `Avstand: ${formatDistance(activeTargetDistanceM)}`
              : mode === 'route_follow'
                ? `Avstand fra rute: ${distanceFromRoute !== null ? formatDistance(distanceFromRoute) : '—'}`
                : 'Ingen punkt i nærheten'}
          </div>
        </div>

        {/* Map view controls */}
        <div className="px-4 py-3 border-b border-stone-100">
          <div className="text-3xs font-semibold uppercase tracking-wide text-stone-400 mb-2">{t("auto.routes.tabletfollowpanel.kartvisning", "Kartvisning")}</div>
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={onToggleViewMode}
              className={`h-10 rounded-xl border text-tiny font-black ${viewMode === '2d' ? 'bg-emerald-50 border-emerald-500 text-emerald-700' : 'bg-white border-stone-200 text-stone-700'}`}
            >
              <span className="inline-flex items-center gap-1"><Mountain size={13} /> {viewMode.toUpperCase()}</span>
            </button>
            <button
              onClick={onToggleMapOrientation}
              className={`h-10 rounded-xl border text-tiny font-black ${mapOrientationMode === 'north_up' ? 'bg-white border-stone-200 text-stone-700' : 'bg-emerald-50 border-emerald-500 text-emerald-700'}`}
            >
              <span className="inline-flex items-center gap-1"><Compass size={13} /> {mapOrientationMode === 'north_up' ? 'Nord opp' : 'Kjøreretning'}</span>
            </button>
          </div>
        </div>

        {/* Waypoint buttons */}
        {onAddWaypoint && (
          <div className="px-4 py-3 border-b border-stone-100">
            <div className="text-3xs font-semibold uppercase tracking-wide text-stone-400 mb-2 flex items-center justify-between">
              <span>Waypoints</span>
              {waypointCount > 0 && (
                <span className="px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-600 text-4xs font-black">
                  {waypointCount}
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {WAYPOINT_BUTTONS.map(({ icon, Icon, color, label }) => (
                <button
                  key={icon}
                  onClick={() => handleAddWaypoint(icon)}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-2xs font-bold transition-all active:scale-95 ${
                    justAdded === icon
                      ? 'bg-emerald-100 text-emerald-600'
                      : 'bg-stone-50 hover:bg-stone-100 text-stone-600'
                  }`}
                >
                  {justAdded === icon ? (
                    <Check size={12} className="text-emerald-500" />
                  ) : (
                    <Icon size={12} className={color} />
                  )}
                  {label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Next POIs */}
        {mode === 'route_follow' && (
          <div className="px-4 py-3 border-b border-stone-100">
            <div className="text-3xs font-semibold uppercase tracking-wide text-stone-400 mb-2">{t("auto.routes.tabletfollowpanel.neste_langs_ruten", "Neste langs ruten")}</div>
            <div className="space-y-2">
              {nextPois.slice(0, 5).map((poi) => {
                const distanceAhead = progressM !== null ? Math.max(0, poi.distanceAlongRoute - progressM) : poi.distanceAlongRoute;
                return (
                  <div key={poi.id} className="rounded-xl bg-stone-50 border border-stone-100 px-2.5 py-2 flex items-center gap-2">
                    <Navigation size={12} className="text-stone-500" />
                    <span className="text-tiny font-bold text-stone-800 flex-1 truncate">{poi.name}</span>
                    <span className="text-2xs text-[var(--text-muted)] whitespace-nowrap">{formatDistance(distanceAhead)}</span>
                  </div>
                );
              })}
              {nextPois.length === 0 && (
                <div className="text-tiny text-[var(--text-secondary)]">{t("auto.routes.tabletfollowpanel.ingen_flere_punkter_pa_ruten", "Ingen flere punkter på ruten.")}</div>
              )}
            </div>
          </div>
        )}

        {/* Elevation profile */}
        {elevationProfile && elevationProfile.length >= 2 && (
          <div className="px-4 py-3">
            <MiniElevationChart profile={elevationProfile} />
          </div>
        )}
      </div>
    </div>
  );
};

export default TabletFollowPanel;
